import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, EventEmitter } from '@angular/core';
import { BDMatrixEliminationCategory } from '@models/bdMatrixEliminationCategory.model';
import { BDMatrixFilter } from '@models/bdMatrixFilter.model';
import { SearchFilterCriteria } from '@models/eliminationMatrixFilterModel';
import { FinancialReview } from '@models/financialReview.model';
import { FinancialReviewFilter } from '@models/financialReviewFilter.model';
import { FinancialReviewScreenResult } from '@models/financialReviewScreenResult.model';
import { MatrixExcelDTO } from '@models/matrixExcelDTO.model';
import { UserNotificationService } from '@services/user-notification.service';
import { saveAs } from 'file-saver-es';
import { LoggingService } from './logging.service';
import { FinancialReviewDatabaseSelection } from '../models/financialReviewDatabaseSelection.model';
import { FinancialReviewDatabase } from '../models/financialReviewDatabase.model';
import { firstValueFrom, Observable } from 'rxjs';
import { FinancialReviewBulkAcceptItem } from '../mystudies/analysis/financialadjustments/financial-review-matrix/fr-bulk-accept-item.model';
import { FinancialReviewDetails } from '../models/financialReviewDetails.model';
import { FinancialDatAdjustmentErrorMessage } from '../shared/_constants';

@Injectable({
  providedIn: 'root',
})
export class FinancialReviewService {
  public comparableIndex: number;
  public comparableCount: number;
  public comparableStatusId: number;
  public allComparables: FinancialReview[] = [];
  public selectedComparableID: number;
  public filter: FinancialReviewFilter = new FinancialReviewFilter();
  public comparablesWithErrors: number[] = [];
  public eliminationCriteriaColumns: BDMatrixEliminationCategory[] = [];
  public isImportCompleted: EventEmitter<boolean> = new EventEmitter();

  constructor(
    private http: HttpClient,
    private logger: LoggingService,
    private userNotificationService: UserNotificationService,
  ) {}

  public async getFinancialReviewMatrix(filter: BDMatrixFilter): Promise<FinancialReviewScreenResult> {
    this.logger.info(this.constructor.name, 'GetFinancialReviewMatrix', 'Start');
    const result = <FinancialReviewScreenResult>await firstValueFrom(
      this.http.post<FinancialReviewScreenResult>('/api/FinancialReview/FinancialReviewResult/', filter),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetFinancialReviewMatrix', e);
    });

    return result;
  }

  public async convertReclassDataForComparable(data: any): Promise<void> {
    await firstValueFrom(this.http.post<void>('/api/FinancialReview/ConvertReclassDataForComparable/', data)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'SaveFinancialReview', e);
    });
  }

  public convertReclassDataForSearchStrategy(data: any): Observable<void> {
    return this.http.post<void>('/api/FinancialReview/ConvertReclassDataForSearchStrategy/', data);
  }

  public updateSelectedCurrencyIds(data: any): Observable<void> {
    return this.http.post<void>('/api/FinancialReview/UpdateSelectedCurrencyIds/', data);
  }

  public isAnyReclassItemAdded(searchStrategyId: number): Observable<boolean> {
    return this.http.get<boolean>(`/api/FinancialReview/IsAnyReclassItemAdded?searchStrategyId=${searchStrategyId}`);
  }

  public async saveFinancialReview(filter: FinancialReview): Promise<void> {
    this.logger.info(this.constructor.name, 'SaveFinancialReview', 'Start');
    await firstValueFrom(this.http.post('/api/FinancialReview/SaveFinancialReview/', filter)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'SaveFinancialReview', e);
    });
  }

  public async bulkAccept(bulkAcceptItems: FinancialReviewBulkAcceptItem[]): Promise<boolean> {
    this.logger.info(this.constructor.name, 'BulkAccept', 'Start', bulkAcceptItems);
    const saveResult: boolean = <boolean>await firstValueFrom(
      this.http.post<boolean>('/api/FinancialReview/BulkAcceptComparable', bulkAcceptItems),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'BulkAccept', e);
    });
    return saveResult;
  }

  public getDetails(
    studyId: number,
    isnext = false,
    isprevious = false,
    isFunctionalCurrency: boolean,
    isConsolidated?: boolean,
    filingTypeId?: number,
  ): Promise<FinancialReviewDetails> {
    const filter = Object.assign({}, this.filter);
    filter.pageSize = 1;
    filter.pageIndex = 0;
    filter.comparableStatusId = 1;
    filter.isConsolidated = isConsolidated;
    filter.isFunctionalCurrency = isFunctionalCurrency;
    filter.filingTypeId = filingTypeId;
    filter.studyId = studyId;

    let selectedComparableId: number;
    let selectedBvdID: string;

    const companies: FinancialReview[] = this.allComparables;
    if (isnext === true) {
      for (let i = 0; i < companies.length; i++) {
        if (selectedComparableId === undefined && this.selectedComparableID === companies[i].comparableID && i + 1 < companies.length) {
          selectedComparableId = companies[i + 1].comparableID;
          selectedBvdID = companies[i + 1].bvDId;
        }
      }
    } else if (isprevious === true) {
      for (let i = 0; i < companies.length; i++) {
        if (selectedComparableId === undefined && this.selectedComparableID === companies[i].comparableID && i - 1 >= 0) {
          selectedComparableId = companies[i - 1].comparableID;
          selectedBvdID = companies[i - 1].bvDId;
        }
      }
    } else {
      for (let i = 0; i < companies.length; i++) {
        if (selectedComparableId === undefined && this.selectedComparableID === companies[i].comparableID) {
          selectedComparableId = companies[i].comparableID;
          selectedBvdID = companies[i].bvDId;
        }
      }
    }

    this.logger.info(this.constructor.name, 'getItem', `filter=${filter}`);

    this.selectedComparableID = selectedComparableId;
    if (this.selectedComparableID && this.selectedComparableID > 0) {
      const filterSearchCriteras: SearchFilterCriteria[] = [];
      const criteria: SearchFilterCriteria = new SearchFilterCriteria();

      criteria.ColumnName = 'BvDId';
      criteria.FilterValue = selectedBvdID;
      criteria.FilterCriteria = 3;
      filterSearchCriteras.push(criteria);
      filter.filterParameter = filterSearchCriteras;
      filter.selectedCompanyIds = this.selectedComparableID.toString();
    } else {
      filter.pageIndex = this.comparableIndex;
    }

    return this.getFinancialReviewDetail(filter);
  }

  async getFinancialReviewDetail(filter: FinancialReviewFilter): Promise<FinancialReviewDetails> {
    this.logger.info(this.constructor.name, 'GetFinancialReviewDetail', 'Start');
    const result = <FinancialReviewDetails>await firstValueFrom(
      this.http.post<FinancialReviewDetails>('/api/FinancialReview/FinancialReviewDetail/', filter),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetFinancialReviewDetail', e);
    });

    return result;
  }

  public async getFinancialDataAdjCount(searchStrategyId: number): Promise<number> {
    this.logger.info(this.constructor.name, 'GetFinancialDataAdjCount', 'Start');
    const result = <number>await firstValueFrom(this.http.get<number>(`/api/FinancialReview/GetFinancialDataAdjCount/${searchStrategyId}`)).catch(
      (e) => {
        this.logger.httpError(this.constructor.name, 'GetFinancialDataAdjCount', e);
      },
    );

    return result;
  }

  public async deleteFinancialDataAdj(searchStrategyId: number): Promise<number> {
    this.logger.info(this.constructor.name, 'DeleteFinancialDataAdj', 'Start');
    const result = <number>await firstValueFrom(this.http.delete<number>(`/api/FinancialReview/DeleteFinancialDataAdj/${searchStrategyId}`)).catch(
      (e) => {
        this.logger.httpError(this.constructor.name, 'DeleteFinancialDataAdj', e);
      },
    );

    return result;
  }

  public deleteFinancialDataAdj$(searchStrategyId: number): Observable<number> {
    return this.http.delete<number>(`/api/FinancialReview/DeleteFinancialDataAdj/${searchStrategyId}`);
  }

  public removeComparableFromErrorList(comparableId: number): void {
    this.comparablesWithErrors = this.comparablesWithErrors.filter((c) => c !== comparableId);
  }

  public async GetFinancialReviewDB(searchStrategyId: number, comparableId: number) {
    this.logger.info(this.constructor.name, 'GetFinancialReviewDB', 'Start');
    const result = <FinancialReviewDatabaseSelection[]>await firstValueFrom(
      this.http.get<FinancialReviewDatabaseSelection[]>(`/api/FinancialReview/GetFinancialReviewDB/${searchStrategyId}/${comparableId}`),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetFinancialReviewDB', e);
    });

    return result;
  }

  public async SaveFinancialReviewDB(financialReviewDB: FinancialReviewDatabase[]) {
    await firstValueFrom(this.http.post<void>('/api/FinancialReview/SaveFinancialReviewDB/', financialReviewDB)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'SaveFinancialReviewDB', e);
    });
  }

  public async GetSingleTypeOfFinancialAvailableForComparable(searchStrategyId: number, comparableId: number): Promise<boolean> {
    this.logger.info(this.constructor.name, 'GetSingleTypeOfFinancialAvailableForComparable', 'Start');
    const result: boolean = <boolean>await firstValueFrom(
      this.http.get<boolean>(`/api/FinancialReview/GetSingleTypeOfFinancialAvailableForComparable/${searchStrategyId}/${comparableId}`),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetSingleTypeOfFinancialAvailableForComparable', e);
    });

    return result;
  }

  public SegmentedFinancialReport(
    studyId: number,
    studySearchStrategyId: number,
    comparableIds: number[],
    IsFromFinancialReviewBigWindow: boolean,
  ): Observable<void> {
    const input = { studyId, studySearchStrategyId, comparableIds, IsFromFinancialReviewBigWindow };
    return this.http.post<void>('/api/financialreview/SegmentedFinancialReport', input) as Observable<void>;
  }

  public async isSegmentedDatabaseAndFeed(searchStrategyId: number): Promise<boolean> {
    const result: boolean = <boolean>await firstValueFrom(
      this.http.get<boolean>(`/api/FinancialReview/IsSegmentedDatabaseAndFeed/${searchStrategyId}`),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'isSegmentedDatabaseAndFeed', e);
    });

    return result;
  }

  async exportMatrix(studyId: number): Promise<boolean> {
    const result = await firstValueFrom(
      this.http.get('/api/FinancialReview/ExportMatrix/' + studyId, {
        responseType: 'blob',
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response',
      }),
    )
      .then((response) => {
        let filename = 'Temp_';
        const contentDispositionHeader = response.headers.get('Content-Disposition');

        if (contentDispositionHeader) {
          filename = contentDispositionHeader.split(';')[1].trim().split('=')[1];
        }

        const blob = new Blob([response.body], { type: response.body.type.toString() });
        saveAs(blob, filename);

        this.userNotificationService.addNotification(`Financial Review Excel file for ${studyId}: Ready to download.`, false);
        return true;
      })
      .catch((e) => {
        this.logger.httpError(this.constructor.name, 'exportMatrix', e);
        if (e.status === 404) {
          this.userNotificationService.addNotification(`Financial Review Excel file failed to download for study ${studyId}: No data found.`, true);
        }

        return false;
      });
    return result;
  }

  async importMatrix(formdata: FormData, returnurl: string, studyId: number): Promise<boolean> {
    const fileName = formdata.get('fileName');
    this.userNotificationService.addNotification(
      `Import in Progress: Your file '${fileName}' is being imported into Financial Review Matrix. Please note that this process may take a few moments, particularly for larger files. for studyId  ${studyId}.`,
      false,
    );

    const result = await firstValueFrom(this.http.post('/api/FinancialReview/importmatrix/', formdata))
      .then(() => {
        this.userNotificationService.addNotification(
          `Import Complete: Your file '${fileName}' has been successfully imported into Financial Review Matrix for studyId  ${studyId}.`,
          false,
        );
        this.isImportCompleted.emit(true);
        return true;
      })
      .catch((e) => {
        if (e.status === 404) {
          this.userNotificationService.addNotification(
            `Import Unsuccessful – to ensure a successful import, kindly verify the following:

            The file you're importing corresponds to the same Search Strategy used at the time of export - any changes to the Search Strategy since the export can lead to a failed import.
            The order and number of columns in your file exactly match those in the exported file.
            There are no duplicate rows with the same Company ID and Company Name in the file.
            No new companies have been added to the list after export for study ${studyId}: No data found.`,
            true,
          );
          return null;
        } else {
          let resultMsg = FinancialDatAdjustmentErrorMessage[e.error.toString()];
          if (!resultMsg) {
            resultMsg = e.error.toString();
          }
          this.userNotificationService.addNotification(
            `Import Unsuccessful – to ensure a successful import, kindly verify the following:

            The file you're importing corresponds to the same Search Strategy used at the time of export - any changes to the Search Strategy since the export can lead to a failed import.
            The order and number of columns in your file exactly match those in the exported file.
            There are no duplicate rows with the same Company ID and Company Name in the file.
            No new companies have been added to the list after export ${studyId}:${resultMsg}`,
            true,
          );
        }
        return false;
      });
    return result;
  }
}
